/**
 * The `tarif_deckung` document — tariff variations and claims configuration — as the typed model
 * the v1-replica editors work on (SDD-018 §5, ui-spec §1–§6).
 *
 * The shape is EIS v1's `ProductConfigData` (api/schemas/asset/tarif_deckung.schema.json), so the
 * same document is what claims-bridge serves to claimservice. Every operation here is PURE: it takes
 * a document and returns the next one, carrying through whatever it does not touch — the asset
 * document's rule (asset-document.ts): a key the editor has no control for is never dropped.
 *
 * v1 behaviours are reproduced on purpose (key slugs, the `_1` suffixes, deductible ↔ calculation
 * step sync, mandatory steps re-added); v1 DEFECTS are not (docs/sdd/claims/ui-spec.md §6 and the
 * v1 reference): pre-filled keys never contain `-`, deleting a category also clears it from the
 * deductibles that applied to it, an import replaces the whole draft.
 */

export type TermType = 'fixed' | 'percent';
export type Period = 'PER_CLAIM' | 'CALENDAR_YEAR' | 'POLICY_YEAR';
export type ProRata = 'ENABLED' | 'ENABLED_AFTER_WAITING_PERIOD' | 'DISABLED';
export type BillingPeriod = 'MONTHLY' | 'YEARLY';
export type SettlementType = 'PARTIAL_CLAIM' | 'COMPLETE_CLAIM';
export type Currency = 'EUR' | 'USD' | 'GBP' | 'CHF';
export type Rounding = 'NONE' | 'NEAREST_0_05';
export type ColumnType = 'string' | 'number' | 'date' | 'dateTime' | 'boolean' | 'select' | 'multiSelect';

export const TERM_TYPES: readonly TermType[] = ['fixed', 'percent'];
/** v1's select order (the enum's declaration order). */
export const PERIODS: readonly Period[] = ['CALENDAR_YEAR', 'POLICY_YEAR', 'PER_CLAIM'];
export const PRO_RATAS: readonly ProRata[] = ['ENABLED', 'ENABLED_AFTER_WAITING_PERIOD', 'DISABLED'];
export const BILLING_PERIODS: readonly BillingPeriod[] = ['MONTHLY', 'YEARLY'];
export const SETTLEMENT_TYPES: readonly SettlementType[] = ['COMPLETE_CLAIM', 'PARTIAL_CLAIM'];
export const CURRENCIES: readonly Currency[] = ['EUR', 'USD', 'GBP', 'CHF'];
export const ROUNDINGS: readonly Rounding[] = ['NONE', 'NEAREST_0_05'];
export const COLUMN_TYPES: readonly ColumnType[] = ['string', 'number', 'date', 'dateTime', 'boolean', 'select', 'multiSelect'];

export interface AmountLimit {
  readonly amount: number;
  readonly limit?: number;
}

/** A variation's overall term (v1 TariffCoverageTerm). */
export interface TariffTerm {
  readonly type: TermType;
  readonly period?: Period;
  readonly value: AmountLimit;
  readonly proRata: ProRata;
}

/** A category's term (v1 CoverageTerm): `{isCovered:false}`, `{isCovered:true}`, or covered + typed. */
export interface CoverageTerm {
  readonly isCovered: boolean;
  readonly type?: TermType;
  readonly period?: Period;
  readonly value?: AmountLimit;
  readonly proRata?: ProRata;
}

export interface CoverageDefinition {
  readonly key: string;
  readonly label: string;
  readonly description?: string;
}

export interface CategoryDefinition {
  readonly key: string;
  readonly label: string;
  readonly description?: string;
  readonly coverageDefinitions: readonly CoverageDefinition[];
}

export interface VariationCategory {
  readonly key: string;
  readonly coverageTerm: CoverageTerm;
  readonly coverages: readonly { readonly key: string; readonly coverageTerm: CoverageTerm }[];
}

export interface Deductible {
  readonly key: string;
  readonly label: string;
  readonly type: TermType;
  readonly period: Period;
  readonly applicableCategoryKeys: readonly string[];
  readonly options: readonly AmountLimit[];
}

/** A cost factor: `insuredObjectId` = object or question group, `productFieldId` = `<object>.<field>`. */
export interface CostFactor {
  readonly insuredObjectId: string;
  readonly productFieldId: string;
  readonly group?: string;
  readonly name?: string;
  readonly value: string | number | boolean;
}

export interface ApprovalLimit {
  readonly roleCode: string;
  readonly amount: number;
}

export type StepType = 'coverage' | 'deductible' | 'manualAdjustment';

export interface CalculationStep {
  readonly key: string;
  readonly order: number;
  readonly type: StepType;
  readonly deductibleKey?: string;
  readonly coverageLevel?: 'tariff' | 'category';
}

export interface WaitingPeriod {
  readonly days: number;
  readonly startFrom: 'POLICY_START_DATE';
  readonly includeStartDate: boolean;
}

export interface VariationInfo {
  readonly label: string;
  readonly description?: string;
  readonly startingPremium?: number;
  readonly billingPeriod?: BillingPeriod;
}

export interface TariffVariation {
  readonly key: string;
  readonly info: VariationInfo;
  readonly coverageTerm: TariffTerm;
  readonly categories: readonly VariationCategory[];
  readonly deductibles: readonly Deductible[];
  readonly productFieldOverrides: readonly CostFactor[];
  readonly approvalLimits: readonly ApprovalLimit[];
  readonly calculationOrder: { readonly steps: readonly CalculationStep[] };
  readonly waitingPeriod: WaitingPeriod;
}

export interface CustomColumn {
  readonly key: string;
  readonly label: string;
  readonly type: ColumnType;
  readonly isRequired?: boolean;
}

export interface GeneralSetting {
  readonly settlementType: SettlementType;
  readonly currency: Currency;
  readonly roundingStrategy: Rounding;
}

export interface ClaimPositionFields {
  readonly customFields: readonly CustomColumn[];
  readonly fieldsOrder: readonly string[];
}

export interface ClaimConfig {
  readonly generalSetting: GeneralSetting;
  readonly claimPositionFields: ClaimPositionFields;
}

/** A TARIF-profile document. */
export interface Deckung {
  readonly profile?: 'TARIF';
  readonly variantField?: string;
  readonly tariffVariations: readonly TariffVariation[];
  readonly categoryDefinitions: readonly CategoryDefinition[];
  readonly claimConfig: ClaimConfig;
}

/**
 * v1's system position columns, in v1's default order (ui-spec §3.2): they can be reordered but
 * not edited or deleted, and a custom column may not reuse their keys.
 */
export const SYSTEM_POSITION_COLUMNS = [
  'procedureDate',
  'category',
  'description',
  'reimbursementRate',
  'cost',
  'initialCoverage',
  'coverageAfterDeductible',
  'manualAdjustmentAmount',
  'status',
  'coverage',
] as const;

/** v1's DEFAULT_CLAIM_POSITION_FIELDS — the columns every `fieldsOrder` must contain. */
export const DEFAULT_POSITION_FIELDS = [
  'category',
  'description',
  'status',
  'coverage',
  'cost',
  'procedureDate',
] as const;

/** Keys a custom column may not take: the system columns and v1's two aliases (`date`, `amount`). */
export const RESERVED_COLUMN_KEYS: ReadonlySet<string> = new Set([...SYSTEM_POSITION_COLUMNS, 'date', 'amount']);

export const isSystemColumn = (key: string): boolean =>
  (SYSTEM_POSITION_COLUMNS as readonly string[]).includes(key);

export const MAX_DEDUCTIBLE_OPTIONS = 10;

// ---------------------------------------------------------------------------------------------
// Reading a stored document. A draft can be `{}` (a fresh asset) or partial while it is written;
// the typed view fills the containers the schema requires, keeping every value it finds.
// ---------------------------------------------------------------------------------------------

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const arrayOf = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

export const DEFAULT_GENERAL_SETTING: GeneralSetting = {
  settlementType: 'PARTIAL_CLAIM',
  currency: 'EUR',
  roundingStrategy: 'NONE',
};

/** v1's `getDefaultWaitingPeriod()`. */
export const DEFAULT_WAITING_PERIOD: WaitingPeriod = { days: 30, startFrom: 'POLICY_START_DATE', includeStartDate: true };

/** The typed view of a stored TARIF document, containers filled. */
export function deckungOf(stored: unknown): Deckung {
  const doc = isObject(stored) ? stored : {};
  const claimConfig = isObject(doc.claimConfig) ? doc.claimConfig : {};
  const generalSetting = isObject(claimConfig.generalSetting) ? claimConfig.generalSetting : {};
  const fields = isObject(claimConfig.claimPositionFields) ? claimConfig.claimPositionFields : {};
  return {
    ...(doc as object),
    tariffVariations: arrayOf<TariffVariation>(doc.tariffVariations),
    categoryDefinitions: arrayOf<CategoryDefinition>(doc.categoryDefinitions),
    claimConfig: {
      ...claimConfig,
      generalSetting: { ...DEFAULT_GENERAL_SETTING, ...(generalSetting as Partial<GeneralSetting>) },
      claimPositionFields: {
        ...fields,
        customFields: arrayOf<CustomColumn>(fields.customFields),
        fieldsOrder: arrayOf<string>(fields.fieldsOrder).length > 0 ? arrayOf<string>(fields.fieldsOrder) : [...SYSTEM_POSITION_COLUMNS],
      },
    },
  };
}

// ---------------------------------------------------------------------------------------------
// Keys (v1 utils/utils.ts generateSlug, the `_1` suffixing; ui-spec §2.1, §2.2, §3.2, §3.3b).
// ---------------------------------------------------------------------------------------------

/** v1's slug: lower case, runs of other characters → `_`, one leading/trailing `_` trimmed. */
export function slugOf(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

/** `base`, else `base_1`, `base_2`, … — the first one not taken. */
export function uniqueKey(base: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let n = 1; ; n += 1) {
    const candidate = `${base}_${n}`;
    if (!used.has(candidate)) return candidate;
  }
}

/**
 * A category or variant key from its label. v1 falls back to `category_<timestamp>-<random>` for a
 * label without letters or digits — a key with `-` the pattern refuses; the console falls back to
 * `<prefix>_<n>` (ui-spec §2.1).
 */
export function keyFromLabel(label: string, prefix: 'category' | 'variant', taken: readonly string[]): string {
  const slug = slugOf(label);
  if (slug !== '') return uniqueKey(slug, taken);
  return uniqueKey(`${prefix}_${taken.length + 1}`, taken);
}

const ALPHANUMERIC = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

/**
 * v1 pre-fills `deductible_<nanoid(8)>`, whose alphabet includes `-` — about one key in eight then
 * fails v1's own pattern. Eight characters from `[A-Za-z0-9]` instead (ui-spec §3.3b).
 */
export function newDeductibleKey(random: () => number = Math.random): string {
  let suffix = '';
  for (let i = 0; i < 8; i += 1) suffix += ALPHANUMERIC[Math.floor(random() * ALPHANUMERIC.length)] ?? 'a';
  return `deductible_${suffix}`;
}

/** lodash `camelCase` as v1 uses it for a column key: words split, diacritics removed, camel-cased. */
export function camelCaseOf(label: string): string {
  const words = label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
    .split(/[^A-Za-z0-9]+/)
    .filter((w) => w !== '');
  // lodash keeps a digit run attached to the letters before it ("ICD10" → "icd10").
  const merged: string[] = [];
  for (const word of words) {
    const last = merged.at(-1);
    if (last !== undefined && /^\d+$/.test(word) && /[A-Za-z]$/.test(last)) merged[merged.length - 1] = last + word;
    else merged.push(word);
  }
  return merged
    .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join('');
}

/**
 * A variant key IS a value of the variant field's enum (C-2) — `BASIC`, `COMFORT` — so upper case is
 * allowed, as the asset schema's `variantKey` allows it. v1 lower-cased typed keys (`^[a-z0-9_]+$`),
 * which would make every enum-backed variant untypeable (SDD-018 §13).
 */
export const VARIANT_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_]*$/;
export const DEDUCTIBLE_KEY_PATTERN = /^[A-Za-z0-9_]+$/;

// ---------------------------------------------------------------------------------------------
// Categories (ui-spec §2.1).
// ---------------------------------------------------------------------------------------------

export function addCategory(doc: Deckung, input: { label: string; description?: string }): Deckung {
  const key = keyFromLabel(input.label, 'category', doc.categoryDefinitions.map((c) => c.key));
  const category: CategoryDefinition = {
    key,
    label: input.label.trim(),
    ...(input.description !== undefined && input.description.trim() !== '' ? { description: input.description.trim() } : {}),
    coverageDefinitions: [],
  };
  return { ...doc, categoryDefinitions: [...doc.categoryDefinitions, category] };
}

export function updateCategory(doc: Deckung, key: string, input: { label: string; description?: string }): Deckung {
  return {
    ...doc,
    categoryDefinitions: doc.categoryDefinitions.map((c) => {
      if (c.key !== key) return c;
      const { description: _drop, ...rest } = c;
      const description = input.description?.trim() ?? '';
      return { ...rest, label: input.label.trim(), ...(description !== '' ? { description } : {}) };
    }),
  };
}

/**
 * Deletes a category from the catalogue AND from every variation (v1), and — beyond v1 — from the
 * deductibles that applied to it, which v1 left dangling for the gate to refuse.
 */
export function deleteCategory(doc: Deckung, key: string): Deckung {
  return {
    ...doc,
    categoryDefinitions: doc.categoryDefinitions.filter((c) => c.key !== key),
    tariffVariations: doc.tariffVariations.map((v) => ({
      ...v,
      categories: v.categories.filter((c) => c.key !== key),
      deductibles: v.deductibles.map((d) => ({ ...d, applicableCategoryKeys: d.applicableCategoryKeys.filter((k) => k !== key) })),
    })),
  };
}

// ---------------------------------------------------------------------------------------------
// Variants (ui-spec §2.2).
// ---------------------------------------------------------------------------------------------

export interface VariantInput {
  readonly label: string;
  /** Typed key, or '' for the slug of the label. Ignored when editing (the key is read-only). */
  readonly key: string;
  readonly description: string;
  readonly startingPremium: number | undefined;
  readonly billingPeriod: BillingPeriod | undefined;
  readonly coverageTerm: TariffTerm;
}

/** v1's add defaults: Fest, 0, Pro Schaden, Pro rata. */
export const DEFAULT_TARIFF_TERM: TariffTerm = { type: 'fixed', value: { amount: 0 }, period: 'PER_CLAIM', proRata: 'ENABLED' };

/** v1 `buildTariffInfo`: description trimmed and dropped if empty; the other two only when set. */
function infoOf(input: VariantInput): VariationInfo {
  const description = input.description.trim();
  return {
    label: input.label.trim(),
    ...(description !== '' ? { description } : {}),
    ...(input.startingPremium !== undefined ? { startingPremium: input.startingPremium } : {}),
    ...(input.billingPeriod !== undefined ? { billingPeriod: input.billingPeriod } : {}),
  };
}

/** The term as written: `limit` only for percent (D-2: required there). */
export function normalisedTerm(term: TariffTerm): TariffTerm {
  const value: AmountLimit =
    term.type === 'percent' ? { amount: term.value.amount, limit: term.value.limit ?? 0 } : { amount: term.value.amount };
  return { ...term, value };
}

/** The three mandatory steps in v1's default order. */
export function defaultCalculationSteps(): CalculationStep[] {
  return [
    { key: 'coverage_tariff', order: 0, type: 'coverage', coverageLevel: 'tariff' },
    { key: 'coverage_category', order: 1, type: 'coverage', coverageLevel: 'category' },
    { key: 'manualAdjustment', order: 2, type: 'manualAdjustment' },
  ];
}

export function addVariant(doc: Deckung, input: VariantInput): Deckung {
  const taken = doc.tariffVariations.map((v) => v.key);
  const key = input.key.trim() !== '' ? input.key.trim() : keyFromLabel(input.label, 'variant', taken);
  const variation: TariffVariation = {
    key,
    info: infoOf(input),
    coverageTerm: normalisedTerm(input.coverageTerm),
    categories: [],
    deductibles: [],
    productFieldOverrides: [],
    approvalLimits: [],
    calculationOrder: { steps: defaultCalculationSteps() },
    waitingPeriod: DEFAULT_WAITING_PERIOD,
  };
  return { ...doc, tariffVariations: [...doc.tariffVariations, variation] };
}

export function updateVariant(doc: Deckung, key: string, input: VariantInput): Deckung {
  return mapVariant(doc, key, (v) => ({ ...v, info: { ...infoOf(input) }, coverageTerm: normalisedTerm(input.coverageTerm) }));
}

export function deleteVariant(doc: Deckung, key: string): Deckung {
  return { ...doc, tariffVariations: doc.tariffVariations.filter((v) => v.key !== key) };
}

export function mapVariant(doc: Deckung, key: string, change: (v: TariffVariation) => TariffVariation): Deckung {
  return { ...doc, tariffVariations: doc.tariffVariations.map((v) => (v.key === key ? change(v) : v)) };
}

export const setVariantField = (doc: Deckung, field: string | undefined): Deckung => {
  const { variantField: _drop, ...rest } = doc;
  return field === undefined || field === '' ? (rest) : { ...rest, variantField: field };
};

/** VARIANT_KEYS_MISMATCH, as the gate words it: enum values without a variant, variants without a value. */
export function variantKeyMismatch(doc: Deckung, enumValues: readonly string[]): { missing: string[]; extra: string[] } {
  const keys = doc.tariffVariations.map((v) => v.key);
  return {
    missing: enumValues.filter((value) => !keys.includes(value)),
    extra: keys.filter((key) => !enumValues.includes(key)),
  };
}

// ---------------------------------------------------------------------------------------------
// A variation's categories (ui-spec §2.3).
// ---------------------------------------------------------------------------------------------

export interface CategoryTermInput {
  readonly type: TermType;
  readonly amount: number;
  readonly limit: number | undefined;
  readonly period: Period;
}

/** What the dialog writes: always covered, `limit` only for percent. */
export function coveredTerm(input: CategoryTermInput): CoverageTerm {
  return {
    isCovered: true,
    type: input.type,
    period: input.period,
    value: input.type === 'percent' ? { amount: input.amount, limit: input.limit ?? 0 } : { amount: input.amount },
  };
}

export function assignCategory(doc: Deckung, variant: string, category: string, input: CategoryTermInput): Deckung {
  return mapVariant(doc, variant, (v) => ({
    ...v,
    categories: [...v.categories, { key: category, coverageTerm: coveredTerm(input), coverages: [] }],
  }));
}

export function updateAssignedCategory(doc: Deckung, variant: string, category: string, input: CategoryTermInput): Deckung {
  return mapVariant(doc, variant, (v) => ({
    ...v,
    categories: v.categories.map((c) => (c.key === category ? { ...c, coverageTerm: coveredTerm(input) } : c)),
  }));
}

export function unassignCategory(doc: Deckung, variant: string, category: string): Deckung {
  return mapVariant(doc, variant, (v) => ({
    ...v,
    categories: v.categories.filter((c) => c.key !== category),
    deductibles: v.deductibles.map((d) => ({ ...d, applicableCategoryKeys: d.applicableCategoryKeys.filter((k) => k !== category) })),
  }));
}

/** The catalogue categories this variation has not assigned yet, in catalogue order. */
export function unassignedCategories(doc: Deckung, variant: TariffVariation): CategoryDefinition[] {
  const assigned = new Set(variant.categories.map((c) => c.key));
  return doc.categoryDefinitions.filter((c) => !assigned.has(c.key));
}

// ---------------------------------------------------------------------------------------------
// Cost factors (ui-spec §2.4).
// ---------------------------------------------------------------------------------------------

/** A core-config field a cost factor can target. */
export interface OverridableField {
  /** `<object>.<field>` */
  readonly path: string;
  readonly object: string;
  readonly objectLabel: string;
  readonly field: string;
  readonly fieldLabel: string;
  /** The core-config type: number, integer, money, boolean, string, enum, date. */
  readonly type: string;
}

/** v1 coercion: number → finite Number; boolean → true/1, false/0; else the trimmed string. */
export function coerceValue(raw: string, type: string): string | number | boolean | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  switch (type) {
    case 'number':
    case 'integer':
    case 'money': {
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : undefined;
    }
    case 'boolean':
      if (trimmed === 'true' || trimmed === '1') return true;
      if (trimmed === 'false' || trimmed === '0') return false;
      return undefined;
    default:
      return trimmed;
  }
}

export function costFactorOf(field: OverridableField, value: string | number | boolean): CostFactor {
  return { insuredObjectId: field.object, productFieldId: field.path, group: field.objectLabel, name: field.field, value };
}

export function hasCostFactor(variant: TariffVariation, path: string, except?: string): boolean {
  return variant.productFieldOverrides.some((o) => o.productFieldId === path && o.productFieldId !== except);
}

export function addCostFactor(doc: Deckung, variant: string, factor: CostFactor): Deckung {
  return mapVariant(doc, variant, (v) => ({ ...v, productFieldOverrides: [...v.productFieldOverrides, factor] }));
}

export function updateCostFactor(doc: Deckung, variant: string, previousPath: string, factor: CostFactor): Deckung {
  return mapVariant(doc, variant, (v) => ({
    ...v,
    productFieldOverrides: v.productFieldOverrides.map((o) => (o.productFieldId === previousPath ? factor : o)),
  }));
}

export function deleteCostFactor(doc: Deckung, variant: string, path: string): Deckung {
  return mapVariant(doc, variant, (v) => ({ ...v, productFieldOverrides: v.productFieldOverrides.filter((o) => o.productFieldId !== path) }));
}

// ---------------------------------------------------------------------------------------------
// The calculation order (ui-spec §3.3c, v1 ensureCalculationOrderForDeductibles).
// ---------------------------------------------------------------------------------------------

const isTariffStep = (s: CalculationStep): boolean => s.type === 'coverage' && s.coverageLevel === 'tariff';
const isCategoryStep = (s: CalculationStep): boolean => s.type === 'coverage' && s.coverageLevel === 'category';

/**
 * The steps a variation must have, in a valid order: one tariff coverage, one category coverage,
 * one manual adjustment (missing ones re-added at their default position), one step per deductible
 * (a missing one appended, an orphan dropped), duplicates dropped, renumbered 0..n.
 */
export function normaliseSteps(steps: readonly CalculationStep[], deductibles: readonly Deductible[]): CalculationStep[] {
  const keys = new Set(deductibles.map((d) => d.key));
  const seen = new Set<string>();
  const kept: CalculationStep[] = [];
  for (const step of [...steps].sort((a, b) => a.order - b.order)) {
    const identity = isTariffStep(step)
      ? 'tariff'
      : isCategoryStep(step)
        ? 'category'
        : step.type === 'manualAdjustment'
          ? 'manual'
          : step.type === 'deductible' && step.deductibleKey !== undefined && keys.has(step.deductibleKey)
            ? `deductible:${step.deductibleKey}`
            : undefined;
    if (identity === undefined || seen.has(identity)) continue;
    seen.add(identity);
    kept.push(step);
  }
  const defaults = defaultCalculationSteps();
  // Missing mandatory steps go back where the default order puts them.
  for (const [index, identity] of (['tariff', 'category', 'manual'] as const).entries()) {
    const step = defaults[index];
    if (!seen.has(identity) && step !== undefined) kept.splice(Math.min(index, kept.length), 0, step);
  }
  for (const deductible of deductibles) {
    if (!seen.has(`deductible:${deductible.key}`)) kept.push(deductibleStep(deductible.key, 0));
  }
  return kept.map((step, order) => ({ ...step, order }));
}

export const deductibleStep = (key: string, order: number): CalculationStep => ({
  key: `deductible:${key}`,
  order,
  type: 'deductible',
  deductibleKey: key,
});

/** Moves a step (drag or the keyboard buttons) and renumbers. */
export function moveStep(variant: TariffVariation, from: number, to: number): TariffVariation {
  const steps = [...variant.calculationOrder.steps].sort((a, b) => a.order - b.order);
  const [moved] = steps.splice(from, 1);
  if (moved === undefined) return variant;
  steps.splice(Math.max(0, Math.min(to, steps.length)), 0, moved);
  return { ...variant, calculationOrder: { steps: steps.map((s, order) => ({ ...s, order })) } };
}

// ---------------------------------------------------------------------------------------------
// Deductibles (ui-spec §3.3b). Adding, renaming or deleting one keeps the calculation order in step.
// ---------------------------------------------------------------------------------------------

export function upsertDeductible(variant: TariffVariation, deductible: Deductible, previousKey?: string): TariffVariation {
  const exists = previousKey !== undefined && variant.deductibles.some((d) => d.key === previousKey);
  const deductibles = exists
    ? variant.deductibles.map((d) => (d.key === previousKey ? deductible : d))
    : [...variant.deductibles, deductible];
  // A renamed deductible keeps its step's POSITION: rename the step in place first.
  const renamed =
    exists && previousKey !== deductible.key
      ? variant.calculationOrder.steps.map((s) =>
          s.type === 'deductible' && s.deductibleKey === previousKey ? { ...s, key: `deductible:${deductible.key}`, deductibleKey: deductible.key } : s,
        )
      : variant.calculationOrder.steps;
  return { ...variant, deductibles, calculationOrder: { steps: normaliseSteps(renamed, deductibles) } };
}

export function deleteDeductible(variant: TariffVariation, key: string): TariffVariation {
  const deductibles = variant.deductibles.filter((d) => d.key !== key);
  return { ...variant, deductibles, calculationOrder: { steps: normaliseSteps(variant.calculationOrder.steps, deductibles) } };
}

/** Switching a deductible to Fest strips every limit (v1); to Prozent adds a 0 limit where missing (D-2). */
export function withTermType(options: readonly AmountLimit[], type: TermType): AmountLimit[] {
  return options.map((o) => (type === 'fixed' ? { amount: o.amount } : { amount: o.amount, limit: o.limit ?? 0 }));
}

export const duplicateAmounts = (options: readonly AmountLimit[]): Set<number> => {
  const seen = new Set<number>();
  const duplicated = new Set<number>();
  for (const o of options) {
    if (seen.has(o.amount)) duplicated.add(o.amount);
    seen.add(o.amount);
  }
  return duplicated;
};

/**
 * Which core-config field selects a policy's deductible option (D-4): a field whose NAME equals the
 * deductible key, of a numeric or enum (lookup) type. `undefined`: the first option applies.
 */
export function deductibleSelectionField(
  fields: readonly { readonly path: string; readonly field: string; readonly type: string }[],
  deductibleKey: string,
): string | undefined {
  return fields.find((f) => f.field === deductibleKey && ['number', 'integer', 'money', 'enum'].includes(f.type))?.path;
}

// ---------------------------------------------------------------------------------------------
// Approval limits (ui-spec §3.3d) and the waiting period (§3.3a).
// ---------------------------------------------------------------------------------------------

export function upsertApprovalLimit(variant: TariffVariation, limit: ApprovalLimit, previousRole?: string): TariffVariation {
  const exists = previousRole !== undefined && variant.approvalLimits.some((l) => l.roleCode === previousRole);
  return {
    ...variant,
    approvalLimits: exists ? variant.approvalLimits.map((l) => (l.roleCode === previousRole ? limit : l)) : [...variant.approvalLimits, limit],
  };
}

export const deleteApprovalLimit = (variant: TariffVariation, role: string): TariffVariation => ({
  ...variant,
  approvalLimits: variant.approvalLimits.filter((l) => l.roleCode !== role),
});

export const setWaitingPeriod = (variant: TariffVariation, waitingPeriod: WaitingPeriod): TariffVariation => ({ ...variant, waitingPeriod });

// ---------------------------------------------------------------------------------------------
// Position columns (ui-spec §3.2).
// ---------------------------------------------------------------------------------------------

export type ColumnKeyProblem = 'reserved' | 'duplicate' | 'empty';

/** The key a new column's label yields, and why it cannot be used — if it cannot. */
export function columnKeyFor(fields: ClaimPositionFields, label: string): { key: string; problem?: ColumnKeyProblem } {
  const key = camelCaseOf(label);
  if (key === '') return { key, problem: 'empty' };
  if (RESERVED_COLUMN_KEYS.has(key)) return { key, problem: 'reserved' };
  if (fields.customFields.some((c) => c.key === key)) return { key, problem: 'duplicate' };
  return { key };
}

export function mapClaimConfig(doc: Deckung, change: (c: ClaimConfig) => ClaimConfig): Deckung {
  return { ...doc, claimConfig: change(doc.claimConfig) };
}

export const addColumn = (fields: ClaimPositionFields, column: CustomColumn): ClaimPositionFields => ({
  ...fields,
  customFields: [...fields.customFields, column],
  fieldsOrder: [...fields.fieldsOrder, column.key],
});

/** Editing keeps the key (v1 computes it on add only). */
export const updateColumn = (fields: ClaimPositionFields, key: string, change: Omit<CustomColumn, 'key'>): ClaimPositionFields => ({
  ...fields,
  customFields: fields.customFields.map((c) => (c.key === key ? { ...c, ...change, key } : c)),
});

export const deleteColumn = (fields: ClaimPositionFields, key: string): ClaimPositionFields => ({
  ...fields,
  customFields: fields.customFields.filter((c) => c.key !== key),
  fieldsOrder: fields.fieldsOrder.filter((k) => k !== key),
});

export function moveColumn(fields: ClaimPositionFields, from: number, to: number): ClaimPositionFields {
  const order = [...fields.fieldsOrder];
  const [moved] = order.splice(from, 1);
  if (moved === undefined) return fields;
  order.splice(Math.max(0, Math.min(to, order.length)), 0, moved);
  return { ...fields, fieldsOrder: order };
}

/**
 * The rows the column list draws, in `fieldsOrder`: system columns (their type and required flag
 * from v1's SYSTEM_FIELD_DEFAULTS), then custom ones. A custom column whose key is a system key — the
 * golden documents carry the defaults that way — is the SYSTEM column.
 */
export interface ColumnRow {
  readonly key: string;
  readonly system: boolean;
  readonly label: string;
  readonly type: ColumnType;
  readonly isRequired: boolean;
}

const SYSTEM_DEFAULTS: Record<string, { type: ColumnType; isRequired: boolean }> = {
  procedureDate: { type: 'date', isRequired: true },
  category: { type: 'select', isRequired: true },
  description: { type: 'string', isRequired: false },
  reimbursementRate: { type: 'number', isRequired: false },
  cost: { type: 'number', isRequired: true },
  initialCoverage: { type: 'number', isRequired: false },
  coverageAfterDeductible: { type: 'number', isRequired: false },
  manualAdjustmentAmount: { type: 'number', isRequired: false },
  status: { type: 'select', isRequired: false },
  coverage: { type: 'number', isRequired: false },
};

export function columnRows(fields: ClaimPositionFields): ColumnRow[] {
  return fields.fieldsOrder.map((key) => {
    const custom = fields.customFields.find((c) => c.key === key);
    if (isSystemColumn(key)) {
      const defaults = SYSTEM_DEFAULTS[key] ?? { type: 'string', isRequired: false };
      return { key, system: true, label: key, type: defaults.type, isRequired: defaults.isRequired };
    }
    return { key, system: false, label: custom?.label ?? key, type: custom?.type ?? 'string', isRequired: custom?.isRequired === true };
  });
}

// ---------------------------------------------------------------------------------------------
// General settings (ui-spec §3.1).
// ---------------------------------------------------------------------------------------------

export const setGeneralSetting = (doc: Deckung, change: Partial<GeneralSetting>): Deckung =>
  mapClaimConfig(doc, (c) => ({ ...c, generalSetting: { ...c.generalSetting, ...change } }));

// ---------------------------------------------------------------------------------------------
// The overview (ui-spec §4) and the generation preview (ui-spec §10): DERIVED, never authored.
// ---------------------------------------------------------------------------------------------

/** A category's term is technical (never offered) when it is a fixed amount of 0 — "Nicht gedeckt". */
export function isTechnicalTerm(term: CoverageTerm): boolean {
  return term.isCovered && term.type === 'fixed' && (term.value?.amount ?? 0) === 0;
}

/** Covered, offerable categories of a variation in catalogue order. */
export function coveredCategories(doc: Deckung, variant: TariffVariation): CategoryDefinition[] {
  const byKey = new Map(variant.categories.map((c) => [c.key, c] as const));
  return doc.categoryDefinitions.filter((definition) => {
    const assigned = byKey.get(definition.key);
    return assigned !== undefined && assigned.coverageTerm.isCovered && !isTechnicalTerm(assigned.coverageTerm);
  });
}

/** v1's count in the overview header: every selectable OPTION across the variation's deductibles. */
export const selectableDeductibleCount = (variant: TariffVariation): number =>
  variant.deductibles.reduce((sum, d) => sum + d.options.length, 0);

/** The lowest option over all deductibles, and its type — "Selbstbehalt 0 €" / "Selbstbehalt 10 %". */
export function lowestDeductible(variant: TariffVariation): { amount: number; type: TermType } | undefined {
  let best: { amount: number; type: TermType } | undefined;
  for (const d of variant.deductibles) {
    for (const o of d.options) {
      if (best === undefined || o.amount < best.amount) best = { amount: o.amount, type: d.type };
    }
  }
  return best;
}

export type PreviewLine = { readonly kind: 'category'; readonly label: string } | { readonly kind: 'deductible'; readonly amount: number; readonly type: TermType } | { readonly kind: 'more'; readonly count: number };

/**
 * A variant card's green-check lines (ui-spec §10): covered categories in catalogue order, then the
 * lowest deductible; at most three lines, the third one "+N weitere" when more exist.
 */
export function previewLines(doc: Deckung, variant: TariffVariation): PreviewLine[] {
  const all: PreviewLine[] = coveredCategories(doc, variant).map((c) => ({ kind: 'category', label: c.label }));
  const lowest = lowestDeductible(variant);
  if (lowest !== undefined) all.push({ kind: 'deductible', amount: lowest.amount, type: lowest.type });
  if (all.length <= 3) return all;
  return [...all.slice(0, 2), { kind: 'more', count: all.length - 2 }];
}

// ---------------------------------------------------------------------------------------------
// Import / export (ui-spec §3 top bar). The whole document either way; the console emits YAML and
// never parses it — a YAML import goes to the gateway, which reads `application/yaml` (A-3, C-15).
// ---------------------------------------------------------------------------------------------

export const exportFileName = (tarifSlug: string, version: string, extension: 'yaml' | 'json'): string =>
  `tarif-deckung-${tarifSlug}-${version}.${extension}`;

const PLAIN_SCALAR = /^[A-Za-z_][A-Za-z0-9_ .\-/()&+]*$/;
const YAML_RESERVED = /^(true|false|null|yes|no|on|off|~|y|n)$/i;

function yamlScalar(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value !== 'string') return JSON.stringify(value);
  const text = value;
  if (text !== '' && PLAIN_SCALAR.test(text) && !YAML_RESERVED.test(text) && !text.endsWith(' ') && !/: |#/.test(text)) return text;
  return JSON.stringify(text);
}

/** A JSON value as block YAML (2-space indent, insertion order). Every string a JSON one would be. */
export function toYaml(value: unknown, indent = 0): string {
  const pad = ' '.repeat(indent);
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    return value
      .map((item) => {
        if (isObject(item) && Object.keys(item).length > 0) {
          const body = toYaml(item, indent + 2);
          return `${pad}- ${body.slice(indent + 2)}`;
        }
        if (Array.isArray(item) && item.length > 0) return `${pad}-\n${toYaml(item, indent + 2)}`;
        return `${pad}- ${isObject(item) ? '{}' : Array.isArray(item) ? '[]' : yamlScalar(item)}`;
      })
      .join('\n');
  }
  if (isObject(value)) {
    const entries = Object.entries(value).filter(([, v]) => v !== undefined);
    if (entries.length === 0) return '{}';
    return entries
      .map(([key, v]) => {
        const name = `${pad}${yamlScalar(key)}:`;
        if ((Array.isArray(v) && v.length > 0) || (isObject(v) && Object.keys(v).length > 0)) return `${name}\n${toYaml(v, indent + 2)}`;
        if (Array.isArray(v)) return `${name} []`;
        if (isObject(v)) return `${name} {}`;
        return `${name} ${yamlScalar(v)}`;
      })
      .join('\n');
  }
  return pad + yamlScalar(value);
}
